import { DataTable } from "@design";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type ContentFilterEventItem, listContentFilterEventsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import type { EventFilters } from "./contentSafety";
import { EventFilterBar } from "./EventFilterBar";
import { useEventColumns } from "./eventColumns";
import { useReveal } from "./useReveal";

const PAGE_SIZE = 20;

/** Rows of one flagged call (one per category) share its eventUuid. */
const rowKey = (event: ContentFilterEventItem) => `${event.eventUuid}-${event.source}-${event.category}`;

/** The events page: back to 1 on any change of filters, including returning to earlier ones (Back, Forward). */
function usePage(filters: EventFilters) {
  const key = JSON.stringify(filters);
  const [paging, setPaging] = useState({ key, page: 1 });
  if (paging.key !== key) setPaging({ key, page: 1 });
  const page = paging.key === key ? paging.page : 1;
  return [page, (next: number) => setPaging({ key, page: next })] as const;
}

/** Flagged categories, newest first, filtered and paged on the server, with the audited reveal. */
export function EventsSection({
  start,
  filters,
  categories,
  onFiltersChange,
}: Readonly<{
  start: string;
  filters: EventFilters;
  categories: string[];
  onFiltersChange: (patch: Partial<EventFilters>) => void;
}>) {
  const { t } = useTranslation("adminContentSafety");
  const [page, setPage] = usePage(filters);
  const reveal = useReveal();
  const columns = useEventColumns(reveal.ask);
  const events = useQuery({
    ...listContentFilterEventsOptions({
      query: {
        start_date: start,
        source: filters.source,
        category: filters.category,
        severity: filters.severity,
        page,
        page_size: PAGE_SIZE,
      },
    }),
    placeholderData: keepPreviousData,
  });
  const filtered = Boolean(filters.source || filters.category || filters.severity);

  return (
    <PageSection title={t("events.title")} description={t("events.description")}>
      <EventFilterBar
        filters={filters}
        categories={categories}
        total={events.data?.meta.total}
        onChange={onFiltersChange}
      />
      {events.isError ? (
        <Text tone="error" role="alert">
          {t("events.loadFailed")}
        </Text>
      ) : (
        // `relative` keeps the table's sr-only header (phones) inside the page's scroller; else it makes the document taller.
        <div className="relative">
          <DataTable
            aria-label={t("events.tableLabel")}
            columns={columns}
            rows={events.data?.data ?? []}
            rowKey={rowKey}
            loading={events.isPending}
            empty={
              <EmptyState icon={<ShieldCheck size={32} />} title={filtered ? t("events.noResults") : t("empty")} />
            }
            pagination={{ page, pageCount: Math.max(1, events.data?.meta.totalPages ?? 1), onPageChange: setPage }}
          />
        </div>
      )}
      {reveal.dialog}
    </PageSection>
  );
}
